import { useLanguage, t } from "../../../i18n";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import EmptyState from "../../../components/common/EmptyState/EmptyState";
import ListingForm, { listingInitialValues } from "../../../components/listing/ListingForm/ListingForm";
import ROUTES from "../../../config/routes";
import { useAuth } from "../../../features/auth/context/AuthContext";
import getRoomListingDetails from "../../../features/listing/services/getRoomListingDetails";
import updateRoomListing from "../../../features/listing/services/updateRoomListing";
import updateRoomListingImages from "../../../features/listing/services/uploadRoomListingImages";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import Footer from "../../../components/layout/Footer/Footer";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
function normalizeUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function normalizeAvailabilityDateForInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function normalizeInitialRoomImages(listing) {
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    return listing.images.map(item => {
      const imageUrl = item?.url || item?.image_path || "";
      const imageId = item?.id ?? item?.image_id ?? "";
      if (!imageUrl) return null;
      return {
        id: String(imageId),
        url: imageUrl,
        isExistingImage: true
      };
    }).filter(Boolean);
  }
  if (listing?.primary_image) {
    return [{
      id: "",
      url: listing.primary_image,
      isExistingImage: true
    }];
  }
  return [];
}
function mapApiListingToInitialValues(listing) {
  const communityPreference = Array.isArray(listing?.community_preferences_data) ? listing.community_preferences_data.map(item => String(item?.id)).filter(Boolean) : Array.isArray(listing?.community_preferences) ? listing.community_preferences.map(item => String(item)) : [];
  return {
    roomTitle: listing?.title || "",
    locationAreaStreet: listing?.address || "",
    district: listing?.neighbourhood?.id !== undefined && listing?.neighbourhood?.id !== null ? String(listing.neighbourhood.id) : "",
    city: listing?.city?.id !== undefined && listing?.city?.id !== null ? String(listing.city.id) : "",
    monthlyRent: listing?.rent !== undefined && listing?.rent !== null ? String(listing.rent) : "",
    availableMode: String(listing?.availability_type || "").toLowerCase() === "immediate" ? "immediate" : "date",
    availableFrom: String(listing?.availability_type || "").toLowerCase() === "date" ? normalizeAvailabilityDateForInput(listing?.available_from) : "",
    houseType: String(listing?.house_type || "").trim().toLowerCase(),
    roomType: String(listing?.room_type || "").trim().toLowerCase(),
    genderPreference: String(listing?.gender_preference || "").trim().toLowerCase(),
    communityPreference,
    bedrooms: listing?.bedrooms !== undefined && listing?.bedrooms !== null ? Number(listing.bedrooms) || 1 : 1,
    bathrooms: listing?.bathrooms !== undefined && listing?.bathrooms !== null ? Number(listing.bathrooms) || 1 : 1,
    kitchenAccess: listing?.kitchen_access !== undefined && listing?.kitchen_access !== null ? String(listing.kitchen_access) : "",
    acType: listing?.ac_type !== undefined && listing?.ac_type !== null ? String(listing.ac_type) : "",
    electricityIncluded: typeof listing?.electricity_included === "boolean" ? listing.electricity_included : false,
    waterBillIncluded: typeof listing?.water_bill_included === "boolean" ? listing.water_bill_included : false,
    parkingAvailable: typeof listing?.parking_available === "boolean" ? listing.parking_available : false,
    currentOccupants: listing?.current_occupants !== undefined && listing?.current_occupants !== null ? Number(listing.current_occupants) || 1 : 1,
    rentPaymentPlan: listing?.rent_payment_plan !== undefined && listing?.rent_payment_plan !== null ? String(listing.rent_payment_plan) : "",
    minimumStay: listing?.minimum_stay !== undefined && listing?.minimum_stay !== null ? Number(listing.minimum_stay) || 1 : 1,
    isFurnished: Boolean(listing?.furnished),
    allowWhatsAppContact: Boolean(listing?.allow_whatsapp),
    allowDirectCalls: Boolean(listing?.allow_calls),
    description: listing?.description || "",
    roomImages: normalizeInitialRoomImages(listing)
  };
}
function getOriginalExistingImageIds(listing) {
  if (!Array.isArray(listing?.images)) return [];
  return listing.images.map(item => item?.id ?? item?.image_id ?? null).filter(item => item !== null && item !== undefined && String(item).trim() !== "").map(item => String(item));
}
function getRemainingExistingImageIds(roomImages = []) {
  return roomImages.filter(item => typeof item === "object" && item !== null && item.isExistingImage === true && (item.id !== undefined || item.image_id !== undefined)).map(item => String(item.id ?? item.image_id ?? "")).filter(Boolean);
}
export default function EditListing() {
  useLanguage();
  useDocumentTitle("Edit Listing");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [currentListing, setCurrentListing] = useState(null);
  const [isListingLoading, setIsListingLoading] = useState(true);
  const [listingError, setListingError] = useState("");
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const {
    user: authUser
  } = useAuth();
  const user = useMemo(() => normalizeUser(authUser || {}), [authUser]);
  const fetchListing = useCallback(async () => {
    try {
      setIsListingLoading(true);
      setListingError("");
      const response = await getRoomListingDetails(id);
      const responseData = response?.data || null;
      if (!responseData) {
        throw new Error(response?.message || "Listing not found.");
      }
      setCurrentListing(responseData);
    } catch (error) {
      setCurrentListing(null);
      setListingError(error?.response?.data?.message || error?.message || "Failed to load listing details.");
    } finally {
      setIsListingLoading(false);
    }
  }, [id]);
  useEffect(() => {
    if (id) {
      fetchListing();
    } else {
      setIsListingLoading(false);
      setListingError("Listing ID is missing.");
    }
  }, [id, fetchListing]);
  const mappedInitialValues = useMemo(() => {
    if (!currentListing) return listingInitialValues;
    return mapApiListingToInitialValues(currentListing);
  }, [currentListing]);
  const handleSubmit = async (payload, roomImageFiles, formikHelpers, values) => {
    try {
      if (formikHelpers?.setSubmitting) {
        formikHelpers.setSubmitting(true);
      }
      const response = await updateRoomListing(id, payload);
      const isSuccess = response?.status === true;
      if (!isSuccess) {
        throw new Error(response?.message || "Failed to update listing.");
      }
      const originalExistingImageIds = getOriginalExistingImageIds(currentListing);
      const remainingExistingImageIds = getRemainingExistingImageIds(values?.roomImages || []);
      const deleteImageIds = originalExistingImageIds.filter(imageId => !remainingExistingImageIds.includes(String(imageId)));
      const hasNewImages = Array.isArray(roomImageFiles) && roomImageFiles.length > 0;
      const hasDeletedImages = Array.isArray(deleteImageIds) && deleteImageIds.length > 0;
      if (hasNewImages || hasDeletedImages) {
        const primaryImageId = remainingExistingImageIds.length > 0 ? remainingExistingImageIds[0] : "";
        const imageUpdateResponse = await updateRoomListingImages(id, {
          images: hasNewImages ? roomImageFiles : [],
          deleteImageIds,
          primaryImageId,
          replaceAll: false
        });
        const imageUpdateSuccess = imageUpdateResponse?.status === true;
        if (!imageUpdateSuccess) {
          throw new Error(imageUpdateResponse?.message || "Listing updated but image update failed.");
        }
      }
      toast.success(t(response?.message || "Room listing updated successfully"));
      navigate(ROUTES.MY_LISTINGS);
    } catch (error) {
      toast.error(t(error?.response?.data?.message || error?.message || "Failed to update listing."));
    } finally {
      if (formikHelpers?.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
    }
  };
  if (isListingLoading) {
    return <div className="bb-dashboard-layout">
        <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
          <Sidebar user={user} />
        </div>

        <div className="bb-dashboard-layout__content">
          <Header onMenuClick={() => setShowMobileNav(true)} />

          <VerificationNoticeBanner user={user} />

          <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


          <main className="bb-create-listing-main">
            <Container fluid>
              <div className="bb-listings-feed__loader bb-listings-feed__loader--initial">
                <Spinner animation="border" size="sm" />
                <span>{t("Loading listing details...")}</span>
              </div>
            </Container>
          </main>
        </div>
      </div>;
  }
  if (!currentListing) {
    return <div className="bb-dashboard-layout">
        <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
          <Sidebar user={user} />
        </div>

        <div className="bb-dashboard-layout__content">
          <Header onMenuClick={() => setShowMobileNav(true)} />

          <VerificationNoticeBanner user={user} />

          <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


          <main className="bb-create-listing-main">
            <Container fluid>
              <EmptyState title={t("Listing not found")} description={t(listingError || "The listing you want to edit does not exist or is unavailable.")} actionLabel={t("Back to My Posts")} onActionClick={() => navigate(ROUTES.MY_LISTINGS)} />

            </Container>
          </main>
        </div>
      </div>;
  }
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={user} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={user} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


        <main className="bb-create-listing-main">
          <Container fluid>
            <div className="bb-create-listing__back-wrap">
              <Button variant="link" className="bb-create-listing__back-btn" onClick={() => navigate(ROUTES.MY_LISTINGS)}>

                <HiOutlineArrowLeft className="me-2" />{t("Back to My Posts")}

              </Button>
            </div>

            <div className="bb-create-listing-hero">
              <div>
                <h1 className="bb-create-listing-hero__title">{t("Edit Listing")}</h1>
                <p className="bb-create-listing-hero__subtitle">{t("Update your room listing details and keep your post accurate and relevant.")}


                </p>
              </div>
            </div>

            <Card className="bb-create-listing-card">
              <Card.Body>
                <ListingForm initialValues={mappedInitialValues} submitLabel={t("Update Listing")} onSubmit={handleSubmit} onCancel={() => navigate(ROUTES.MY_LISTINGS)} />

              </Card.Body>
            </Card>
          </Container>
        </main>

        <Footer />
      </div>
    </div>;
}
